import { createContext, useContext, useState, useCallback, useMemo, useEffect, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Language, translations } from "@/content/translations";
import { supabase } from "@/integrations/supabase/client";
import { isBot } from "@/lib/isBot";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  isRTL: boolean;
  t: (typeof translations)["en"];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const SUPPORTED_LANGUAGES: Language[] = ["en", "es", "it", "fr", "nl", "pt", "ar", "zh"];
const STORAGE_KEY = "ulture-language";

// Buscadores y herramientas de análisis: siempre la versión en español, la misma que el HTML
// prerenderizado (<html lang="es">, títulos y descripciones). Sin esto Googlebot (navegador en
// inglés) renderizaba el contenido en inglés bajo metadatos en español.
const detectUserLanguage = (): Language => {
  if (isBot()) return "es";
  const browserLang = navigator.language || navigator.languages?.[0] || "en";
  const primaryLang = browserLang.split("-")[0].toLowerCase();
  if (SUPPORTED_LANGUAGES.includes(primaryLang as Language)) {
    return primaryLang as Language;
  }
  return "en";
};

// Deep-set a value in an object using a dot-notation path
function setNestedValue(obj: any, path: string, value: string): void {
  const keys = path.split(".");
  let current = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (current[key] === undefined || current[key] === null || typeof current[key] !== "object") {
      current[key] = {};
    }
    current = current[key];
  }
  current[keys[keys.length - 1]] = value;
}

// Deep clone a plain object (handles nested objects, arrays, primitives)
function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map(deepClone) as unknown as T;
  const clone: any = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      clone[key] = deepClone((obj as any)[key]);
    }
  }
  return clone;
}

// Fusiona en profundidad `override` sobre `base` sin mutar ninguno de los dos.
// Si a un idioma le falta una clave, se queda la del idioma base en lugar de
// `undefined`. Los arrays se sustituyen enteros (no se mezclan por índice) y las
// cadenas vacías se respetan; solo undefined/null caen al valor base.
type PlainObject = Record<string, unknown>;
const isPlainObject = (v: unknown): v is PlainObject =>
  v !== null && typeof v === "object" && !Array.isArray(v);

function deepMerge<T>(base: T, override: unknown): T {
  if (override === undefined || override === null) return deepClone(base);
  if (!isPlainObject(base) || !isPlainObject(override)) return deepClone(override) as T;
  const merged = deepClone(base) as PlainObject;
  for (const key in override) {
    if (Object.prototype.hasOwnProperty.call(override, key)) {
      merged[key] = deepMerge(base[key], override[key]);
    }
  }
  return merged as T;
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => {
    try {
      const savedLang = localStorage.getItem(STORAGE_KEY) as Language | null;
      if (savedLang && SUPPORTED_LANGUAGES.includes(savedLang)) {
        return savedLang;
      }
    } catch {
      // localStorage might not be available
    }
    return detectUserLanguage();
  });

  const handleSetLanguage = (lang: Language) => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // localStorage might not be available
    }
    setLanguage(lang);
  };

  const isRTL = language === "ar";

  // Reflect the active language on <html> so global lang/dir styling applies
  // (e.g. the Arabic font stack and RTL layout are keyed off :lang(ar)/[dir=rtl]).
  // La landing nueva (/, /sobre-nosotros, /partners, /contacto) gestiona su
  // propio idioma (ulture_lang) y es dueña de <html lang/dir> en sus rutas.
  useEffect(() => {
    const LANDING_PATHS = ["/", "/sobre-nosotros", "/partners", "/contacto"];
    if (LANDING_PATHS.includes(window.location.pathname)) return;
    const root = document.documentElement;
    root.lang = language;
    root.dir = isRTL ? "rtl" : "ltr";
  }, [language, isRTL]);

  // Fetch all DB translation overrides for the current language
  const { data: dbOverrides } = useQuery({
    queryKey: ["public-translations", language],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("translations")
        .select("key_path, value")
        .eq("language", language);

      if (error) {
        console.error("Failed to fetch translation overrides:", error);
        return [];
      }
      return data || [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Merge DB overrides on top of static translations.
  // Red de seguridad por clave: el idioma activo se fusiona sobre el inglés, así
  // una clave que falte en un idioma muestra el texto inglés y nunca `undefined`.
  const t = useMemo(() => {
    const base = deepMerge(translations.en, translations[language]);
    if (dbOverrides && dbOverrides.length > 0) {
      for (const override of dbOverrides) {
        setNestedValue(base, override.key_path, override.value);
      }
    }
    return base;
  }, [language, dbOverrides]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, isRTL, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
